import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Button } from 'components/button';
import InputField from 'components/fields/InputField';
import TextArea from 'components/fields/TextArea';
import {
  MdClose,
  MdSecurity,
  MdCheckBox,
  MdCheckBoxOutlineBlank,
} from 'react-icons/md';
import { toast } from 'App';
import { permissionsService } from 'views/services/permissionsService';
import {
  PERMISSION_RESOURCES,
  RESOURCE_CATEGORIES,
} from 'constants/permissions';
import { errorToText } from 'utils/error-to-text';
import { useIsSuperAdmin } from 'hooks/useIsSuperAdmin';

interface CreateRoleFormData {
  name: string;
  description: string;
}

interface CreateRoleModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export const CreateRoleModal: React.FC<CreateRoleModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const [permissions, setPermissions] = useState<
    Record<
      string,
      { read: boolean; create: boolean; update: boolean; delete: boolean }
    >
  >({});
  const [isLoading, setIsLoading] = useState(false);
  const isSuperAdmin = useIsSuperAdmin();

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
  } = useForm<CreateRoleFormData>({
    defaultValues: {
      name: '',
      description: '',
    },
    mode: 'onChange',
  });

  const watchedName = watch('name');
  const watchedDescription = watch('description');

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);

  const handlePermissionChange = (
    resource: string,
    action: string,
    value: boolean
  ) => {
    setPermissions(prev => ({
      ...prev,
      [resource]: {
        ...prev[resource],
        [action]: value,
      },
    }));
  };

  const onSubmit = async (data: CreateRoleFormData) => {
    setIsLoading(true);
    try {
      await permissionsService.createRole({
        name: data.name.trim().toLocaleLowerCase(),
        description: data.description.trim(),
        permissions,
      });

      toast({
        title: 'Success',
        description: 'Role created successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      reset();
      setPermissions({});
      onSuccess();
      onClose();
    } catch (error: unknown) {
      toast({
        title: 'Error',
        description: errorToText(error),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const getResourceDisplayName = (resource: string): string => {
    const resourceKey = Object.keys(PERMISSION_RESOURCES).find(
      key =>
        PERMISSION_RESOURCES[key as keyof typeof PERMISSION_RESOURCES] ===
        resource
    );
    return resourceKey
      ? resourceKey.replace(/_/g, ' ').toLowerCase()
      : resource;
  };

  const getFilteredResourceCategories = () => {
    if (isSuperAdmin) {
      return RESOURCE_CATEGORIES;
    }

    const filteredCategories = {
      ...RESOURCE_CATEGORIES,
      'User Management': RESOURCE_CATEGORIES['User Management'].filter(
        resource => resource !== PERMISSION_RESOURCES.PERMISSION_MANAGEMENT
      ),
    } as const;

    return filteredCategories;
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={e => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 max-h-[90vh] w-full max-w-5xl overflow-hidden">
        {/* Header */}
        <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                <MdSecurity className="w-6 h-6 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <h2 className="text-xl font-bold m-0 text-gray-900 dark:text-white">
                  Create New Role
                </h2>
                <p className="text-gray-600 dark:text-gray-400 text-sm mt-1">
                  Define permissions and access levels for this role
                </p>
              </div>
            </div>
            <button
              className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 p-2 rounded-lg transition-colors duration-200"
              onClick={onClose}
            >
              <MdClose className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 max-h-[calc(90vh-120px)] overflow-y-auto">
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
            {/* Basic Information Section */}
            <div className="bg-gray-50 dark:bg-gray-800/50 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                <div className="w-2 h-2 bg-blue-500 rounded-full mr-3"></div>
                Basic Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <InputField
                    id="role-name"
                    label="Role Name"
                    placeholder="Enter role name"
                    variant="auth"
                    extra=""
                    state={errors.name ? 'error' : undefined}
                    {...register('name', {
                      required: 'Role name is required',
                      maxLength: {
                        value: 50,
                        message:
                          'The new role name cannot be longer than 50 characters.',
                      },
                    })}
                  />
                  {errors.name && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.name.message}
                    </p>
                  )}
                  <p className="text-gray-500 text-xs mt-1">
                    {watchedName?.length || 0}/50 characters
                  </p>
                </div>
                <div>
                  <TextArea
                    id="role-description"
                    label="Description"
                    placeholder="Enter role description"
                    variant="auth"
                    extra=""
                    state={errors.description ? 'error' : undefined}
                    {...register('description', {
                      maxLength: {
                        value: 150,
                        message:
                          'The new role description cannot be longer than 150 characters.',
                      },
                    })}
                  />
                  {errors.description && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.description.message}
                    </p>
                  )}
                  <p className="text-gray-500 text-xs mt-1">
                    {watchedDescription?.length || 0}/150 characters
                  </p>
                </div>
              </div>
            </div>

            {/* Permissions Section */}
            <div className="bg-gray-50 dark:bg-gray-800/50 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6 flex items-center">
                <div className="w-2 h-2 bg-purple-500 rounded-full mr-3"></div>
                Permissions Configuration
              </h3>
              <div className="space-y-6">
                {Object.entries(getFilteredResourceCategories()).map(
                  ([category, resources]) => (
                    <div
                      key={category}
                      className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl p-5 shadow-sm"
                    >
                      <h4 className="font-semibold text-gray-900 dark:text-white mb-4 text-base border-b border-gray-200 dark:border-gray-700 pb-2">
                        {category}
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {resources.map((resource: string) => (
                          <div
                            key={resource}
                            className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 border border-gray-100 dark:border-gray-700"
                          >
                            <label className="text-sm font-medium text-gray-900 dark:text-white block mb-3">
                              {getResourceDisplayName(resource)}
                            </label>
                            <div className="grid grid-cols-2 gap-2">
                              {['read', 'create', 'update', 'delete'].map(
                                action => {
                                  const isChecked =
                                    permissions[resource]?.[
                                      action as keyof (typeof permissions)[typeof resource]
                                    ] || false;
                                  return (
                                    <label
                                      key={action}
                                      className="flex items-center space-x-2 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md p-2 transition-colors duration-150"
                                    >
                                      {isChecked ? (
                                        <MdCheckBox className="w-4 h-4 text-blue-500" />
                                      ) : (
                                        <MdCheckBoxOutlineBlank className="w-4 h-4 text-gray-400" />
                                      )}
                                      <input
                                        type="checkbox"
                                        checked={isChecked}
                                        onChange={e =>
                                          handlePermissionChange(
                                            resource,
                                            action,
                                            e.target.checked
                                          )
                                        }
                                        className="sr-only"
                                      />
                                      <span className="text-xs capitalize text-gray-700 dark:text-gray-300 font-medium">
                                        {action}
                                      </span>
                                    </label>
                                  );
                                }
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200 dark:border-gray-700">
              <Button
                type="button"
                variant="secondary"
                onClick={onClose}
                disabled={isLoading}
                className="px-6 py-2"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isLoading || Object.keys(errors).length > 0}
                className="px-6 py-2"
              >
                {isLoading ? 'Creating...' : 'Create Role'}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
